import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
} from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import {
  AlertController,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonText,
  IonTitle,
  IonToolbar,
  ModalController,
} from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import { logoFacebook, logoGoogle, personAdd } from 'ionicons/icons';
import { catchError, EMPTY } from 'rxjs';
import { tap } from 'rxjs/operators';

import { EMAIL_VALIDATION_ERRORS } from '../../../constants/validation-errors';
import { ENVIRONMENT } from '../../../model/common-environment';
import { AuthenticationService } from '../../../services/authentication.service';
import { ToastService } from '../../../services/toast.service';
import { ValidationErrorsComponent } from '../../validation-errors/validation-errors.component';

@Component({
  templateUrl: './login-page.component.html',
  styleUrls: ['./login-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    IonItem,
    FormsModule,
    ReactiveFormsModule,
    ValidationErrorsComponent,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonButtons,
    IonButton,
    IonContent,
    IonLabel,
    IonIcon,
    IonText,
    IonInput,
  ],
})
export class LoginPageComponent {
  loginError: string | undefined;
  loginForm = new UntypedFormGroup({
    email: new UntypedFormControl('', [Validators.required, Validators.email]),
    password: new UntypedFormControl('', [Validators.required]),
  });
  readonly emailValidationErrors = EMAIL_VALIDATION_ERRORS;
  readonly thirdAuthProvidersActivated: boolean;
  private readonly authenticationService = inject(AuthenticationService);
  private readonly modalController = inject(ModalController);
  private readonly alertController = inject(AlertController);
  private readonly toastService = inject(ToastService);
  private readonly environment = inject(ENVIRONMENT);

  constructor() {
    addIcons({ logoFacebook, logoGoogle, personAdd });
    effect(() => {
      if (this.authenticationService.loggedInStatus()) {
        this.dismiss();
      }
    });
    this.thirdAuthProvidersActivated =
      this.environment.featureFlipping.thirdPartyAuthentication;
  }

  login(): void {
    const { email, password } = this.loginForm.value;
    this.authenticationService
      .login(email, password)
      .pipe(
        tap(() => {
          this.modalController.dismiss();
          this.toastService.successLoggedIn();
        }),
        catchError((error) => {
          if (error) {
            this.loginError = 'Email ou mot de passe invalide';
          }
          return EMPTY;
        }),
      )
      .subscribe();
  }

  signup(): void {
    this.modalController.dismiss(1);
  }

  loginWithGoogle(): void {
    this.authenticationService.googleLogin();
  }

  loginWithFacebook(): void {
    this.authenticationService.facebookLogin();
  }

  forgotPassword(): void {
    const email = this.loginForm.get('email')?.value;
    this.modalController.dismiss();
    this.authenticationService
      .forgotPassword(email)
      .pipe(
        tap(async () => {
          const alert = await this.alertController.create({
            header: 'Email envoyé',
            message: `Si vous possédez un compte. Un mail de réinitialisation vous à été envoyé.`,
            buttons: [
              {
                text: 'Ok',
                handler: () => {
                  this.modalController.dismiss();
                },
              },
            ],
          });
          await alert.present();
        }),
      )
      .subscribe();
  }

  dismiss(): void {
    this.modalController.dismiss();
  }

  async presentAlertForgotPassword(): Promise<void> {
    const email = this.loginForm.get('email')?.value;
    const alert = await this.alertController.create({
      header: 'Mot de passe oublié',
      message: `Envoyer un mail de réinitialisation à ${email} ?`,
      buttons: [
        { text: 'Non', role: 'cancel', cssClass: 'secondary' },
        {
          text: 'Oui',
          handler: () => {
            this.forgotPassword();
          },
        },
      ],
    });
    await alert.present();
  }
}
